import React from "react";
import { X, ArrowRight, Sparkles, Layers } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MapFloatingSummaryCardSurfaceProps } from "../../contracts/map-workspace";

export function MapFloatingSummaryCardSurface({
  title,
  subtitle,
  badges = [],
  summary = [],
  actions = [],
  onAction,
  onInspect,
  onClear,
  className,
}: MapFloatingSummaryCardSurfaceProps) {
  const hasContent = badges.length > 0 || summary.length > 0 || actions.length > 0;

  return (
    <div
      className={cn(
        "flex flex-col w-72 bg-background border border-border rounded-lg shadow-[0_10px_40px_rgb(0,0,0,0.15)] overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300",
        className
      )}
    >
      {/* Visual Header / Accent */}
      <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/50 to-primary" />

      {/* Header */}
      <div className="flex items-start justify-between p-4 pb-3">
        <div className="flex flex-col min-w-0">
          <h3 className="text-sm font-black text-foreground leading-tight truncate tracking-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="text-[12px] text-muted-foreground font-medium mt-1 leading-normal line-clamp-2">
              {subtitle}
            </p>
          )}
        </div>
        {onClear && (
          <button
            onClick={onClear}
            className="p-1.5 -mt-1 -mr-1 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Content Body */}
      {hasContent && (
        <div className="flex flex-col p-4 pt-0 gap-4">
          {/* Badges Section */}
          {badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {badges.map((badge, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground"
                >
                  {badge}
                </span>
              ))}
            </div>
          )}

          {/* Summary Grid */}
          {summary.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-muted/20 border border-border/50">
              {summary.map((item) => (
                <div key={item.key} className="flex flex-col gap-0.5">
                  <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-widest">
                    {item.label}
                  </span>
                  <span className="text-xs font-mono font-bold text-foreground truncate">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 rounded-md border border-dashed border-border">
              <Sparkles className="w-3.5 h-3.5 text-primary opacity-40" />
              <p className="text-[10px] text-muted-foreground italic">
                No summary metrics available
              </p>
            </div>
          )}

          {/* Actions & Footer */}
          <div className="flex flex-col gap-2 pt-2 border-t border-border/50">
            {actions.map((action) => (
              <button
                key={action.id}
                disabled={action.disabled}
                onClick={() => onAction?.(action.id)}
                className={cn(
                  "flex items-center justify-between w-full px-3 py-2 rounded text-xs font-bold transition-all group",
                  "bg-primary text-primary-foreground hover:brightness-110 active:scale-[0.98]",
                  "disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed disabled:active:scale-100"
                )}
              >
                <span>{action.label}</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </button>
            ))}

            {onInspect && (
              <button
                onClick={onInspect}
                className="flex items-center justify-between w-full px-3 py-2 rounded text-xs font-bold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border border-transparent hover:border-border"
              >
                <span>Full Inspection</span>
                <Layers className="w-3.5 h-3.5 opacity-50" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
